import React, { useContext, useEffect } from 'react'
import { ModalActions } from '../common/modal/Modal'
import { Cost, FinancialYear } from '../../api/schemas/schema'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { t } from '../../i18n/i18n'
import { useForm } from 'react-hook-form'
import { DataContext } from '../../context/DataContext'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { ControlledMoneyInput } from '../common/inputs/ControlledMoneyInput'
import { Button } from '@fluentui/react-components'
import { PostDropdown } from './PostDropdown'
import { IItemState } from '../../interfaces/IItemState'
import { deleteCost, fetchPosts, saveCost } from '../../helpers/financialYearHelper'
import { DraggableModal } from '../common/modal/DraggableModal'
import { MoreOptionsMenuButton } from '../common/other/MoreOptionsMenuButton'
import { isSystemAdmin } from '../../helpers/permissionHelper'
import { Form } from '../common/form/Form'


type ModalCostProps = {
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
   financialYearState: IItemState<FinancialYear>
   close: () => void
   open: boolean
   selectedCost?: Cost
   disabled?: boolean
}
export const ModalCost = ({ setFinancialYearState, financialYearState, close, open, selectedCost, disabled }: ModalCostProps) => {
   const dataContext = useContext(DataContext)
   const { posts, defaultWorkplace, userAccount } = dataContext.state
   const financialYear = financialYearState.item
   const costForm = useForm<Cost>()
   const {
      control,
      reset,
      handleSubmit,
      formState: { isDirty, isSubmitting },
   } = costForm
   useEffect(() => {
      if (!posts) {
         ;(async () => await fetchPosts(dataContext))()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (selectedCost) {
         reset(selectedCost)
      } else {
         reset({ workplace: defaultWorkplace })
      }
   }, [selectedCost, reset, defaultWorkplace])

   const onSubmit = async (data: Cost) => {
      await saveCost(setFinancialYearState, dataContext, data, financialYear.id, reset, close)
   }

   const closeAndReset = () => {
      close()
      reset()
   }

   const deleteOptions = () => {
      const costInUse = financialYear.actionPlans?.some((ap) => ap.costId === selectedCost.id)
      const userHasPermission = isSystemAdmin(userAccount) || financialYear.owner?.id === userAccount?.id

      const deleteDisabled = !userHasPermission || costInUse
      const deleteTooltipMessage = deleteDisabled
         ? !userHasPermission
            ? t('OnlySystemAdministratorOrOwnerCanDeleteItem', { Item: t('Cost').toLowerCase() })
            : t('ItemIsConnectedToItemAndCantBeDeleted', { ItemA: t('Cost'), Article: t('A').toLowerCase(), ItemB: t('ActionPlan').toLowerCase() })
         : ''

      return (
         <MoreOptionsMenuButton
            item={selectedCost}
            deleteFunction={deleteCost}
            setState={setFinancialYearState}
            closeOnDelete={close}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={t('DeleteItem', { Item: t('Cost').toLowerCase() })}
            deleteConfirmationMessage={t('DeleteItemConfirmationMessage', { Item: t('TheCost').toLocaleLowerCase() })}
         />
      )
   }

   return (
      <DraggableModal
         title={selectedCost ? t('EditCost') : t('AddCost')}
         open={open}
         onClose={closeAndReset}
         secondaryButton={selectedCost ? deleteOptions : null}
      >
         <Form onSubmit={handleSubmit(onSubmit)}>
            {posts ? (
               <Grid>
                  {/* TODO: Enable workplace selection when multi-workplace support is fully implemented */}
                  {/* <GridItem size="12/12">
                     <Field label={t('Workplace')} required>
                        <Dropdown size="large" disabled value={watch('workplace') ? watch('workplace').name : ''}></Dropdown>
                     </Field>
                  </GridItem> */}
                  <GridItem size="12/12">
                     <PostDropdown costForm={costForm} disabled={disabled} />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledMoneyInput label={t('Budget')} control={control} name={'budget'} disabled={disabled} />
                  </GridItem>
                  <GridItem size="6/12">
                     <ControlledMoneyInput label={t('Result')} control={control} name={'result'} disabled={disabled} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ControlledTextInput label={t('Description')} control={control} name={'description'} multiline disabled={disabled} />
                  </GridItem>
                  <GridItem size="12/12">
                     <ModalActions>
                        <Button onClick={closeAndReset} disabled={isSubmitting}>
                           {disabled ? t('Close') : t('Cancel')}
                        </Button>
                        {!disabled && (
                           <Button appearance="primary" disabled={!isDirty || isSubmitting} type="submit">
                              {t(selectedCost ? 'Update' : 'Save')}
                           </Button>
                        )}
                     </ModalActions>
                  </GridItem>
               </Grid>
            ) : null}
         </Form>
      </DraggableModal>
   )
}
